import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useCreateWarranty, useUpdateWarranty } from '@/hooks/useWarranties';
import type { Warranty } from '@/hooks/useWarranties';

const COMPONENT_TYPES = [
  { value: 'panels', label: 'Panels' },
  { value: 'inverter', label: 'Inverter' },
  { value: 'battery', label: 'Battery' },
  { value: 'generator', label: 'Generator' },
  { value: 'workmanship', label: 'Workmanship' },
  { value: 'third_party', label: 'Third Party' },
];

const WARRANTY_TYPES = [
  { value: 'product', label: 'Product' },
  { value: 'performance', label: 'Performance' },
  { value: 'labor', label: 'Labor' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  plantId?: string | null;
  editWarranty?: Warranty;
}

export function AddWarrantyDialog({ open, onOpenChange, propertyId, plantId, editWarranty }: Props) {
  const create = useCreateWarranty();
  const update = useUpdateWarranty();
  const isEdit = !!editWarranty;

  const [componentType, setComponentType] = useState('battery');
  const [warrantyType, setWarrantyType] = useState('product');
  const [manufacturer, setManufacturer] = useState('');
  const [model, setModel] = useState('');
  const [providerName, setProviderName] = useState('');
  const [providerWarrantyId, setProviderWarrantyId] = useState('');
  const [termYears, setTermYears] = useState('');
  const [termCycles, setTermCycles] = useState('');
  const [capacityFloor, setCapacityFloor] = useState('');
  const [warrantyStartDate, setWarrantyStartDate] = useState('');
  const [warrantyEndDate, setWarrantyEndDate] = useState('');
  const [installDate, setInstallDate] = useState('');
  const [registrationUrl, setRegistrationUrl] = useState('');
  const [coverageDetails, setCoverageDetails] = useState('');

  useEffect(() => {
    if (editWarranty) {
      setComponentType(editWarranty.component_type);
      setWarrantyType(editWarranty.warranty_type);
      setManufacturer(editWarranty.manufacturer);
      setModel(editWarranty.model ?? '');
      setProviderName(editWarranty.provider_name);
      setProviderWarrantyId(editWarranty.provider_warranty_id ?? '');
      setTermYears(editWarranty.term_years?.toString() ?? '');
      setTermCycles(editWarranty.term_cycles?.toString() ?? '');
      setCapacityFloor(editWarranty.capacity_floor_percent?.toString() ?? '');
      setWarrantyStartDate(editWarranty.warranty_start_date ?? '');
      setWarrantyEndDate(editWarranty.warranty_end_date ?? '');
      setInstallDate(editWarranty.install_date ?? '');
      setRegistrationUrl(editWarranty.registration_url ?? '');
      setCoverageDetails(editWarranty.coverage_details ?? '');
    }
  }, [editWarranty]);

  // Auto-calculate end date when start date + term years change
  useEffect(() => {
    if (warrantyStartDate && termYears && !isEdit) {
      const start = new Date(warrantyStartDate);
      start.setFullYear(start.getFullYear() + parseInt(termYears, 10));
      setWarrantyEndDate(start.toISOString().slice(0, 10));
    }
  }, [warrantyStartDate, termYears, isEdit]);

  const reset = () => {
    setComponentType('battery');
    setWarrantyType('product');
    setManufacturer('');
    setModel('');
    setProviderName('');
    setProviderWarrantyId('');
    setTermYears('');
    setTermCycles('');
    setCapacityFloor('');
    setWarrantyStartDate('');
    setWarrantyEndDate('');
    setInstallDate('');
    setRegistrationUrl('');
    setCoverageDetails('');
  };

  const handleSubmit = () => {
    if (!manufacturer.trim() || !providerName.trim()) {
      toast.error('Manufacturer and provider name are required');
      return;
    }

    const payload = {
      property_id: propertyId,
      plant_id: plantId ?? null,
      component_type: componentType,
      warranty_type: warrantyType,
      manufacturer: manufacturer.trim(),
      model: model.trim() || null,
      provider_name: providerName.trim(),
      provider_warranty_id: providerWarrantyId.trim() || null,
      term_years: termYears ? parseInt(termYears, 10) : null,
      term_cycles: termCycles ? parseInt(termCycles, 10) : null,
      capacity_floor_percent: capacityFloor ? parseFloat(capacityFloor) : null,
      warranty_start_date: warrantyStartDate || null,
      warranty_end_date: warrantyEndDate || null,
      install_date: installDate || null,
      registration_url: registrationUrl.trim() || null,
      coverage_details: coverageDetails.trim() || null,
      serial_numbers: null as string[] | null,
      registration_status: 'unregistered' as string,
      document_paths: [] as string[],
    };

    if (isEdit) {
      update.mutate(
        { id: editWarranty.id, propertyId, updates: payload },
        {
          onSuccess: () => {
            toast.success('Warranty updated');
            onOpenChange(false);
          },
          onError: (err) => toast.error(`Failed to update: ${err.message}`),
        },
      );
    } else {
      create.mutate(payload, {
        onSuccess: () => {
          toast.success('Warranty added');
          reset();
          onOpenChange(false);
        },
        onError: (err) => toast.error(`Failed to add: ${err.message}`),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Warranty' : 'Add Warranty'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="componentType">Component</Label>
              <Select value={componentType} onValueChange={setComponentType}>
                <SelectTrigger id="componentType"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMPONENT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="warrantyType">Warranty Type</Label>
              <Select value={warrantyType} onValueChange={setWarrantyType}>
                <SelectTrigger id="warrantyType"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WARRANTY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="manufacturer">Manufacturer *</Label>
              <Input id="manufacturer" value={manufacturer} onChange={e => setManufacturer(e.target.value)} placeholder="e.g. Sol-Ark" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="model">Model</Label>
              <Input id="model" value={model} onChange={e => setModel(e.target.value)} placeholder="e.g. 15K-2P" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="providerName">Provider *</Label>
              <Input id="providerName" value={providerName} onChange={e => setProviderName(e.target.value)} placeholder="e.g. Solar Insure" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="providerWarrantyId">OEM Reference #</Label>
              <Input id="providerWarrantyId" value={providerWarrantyId} onChange={e => setProviderWarrantyId(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="termYears">Term (years)</Label>
              <Input id="termYears" type="number" value={termYears} onChange={e => setTermYears(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="termCycles">Term (cycles)</Label>
              <Input id="termCycles" type="number" value={termCycles} onChange={e => setTermCycles(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="capacityFloor">Capacity Floor %</Label>
              <Input id="capacityFloor" type="number" value={capacityFloor} onChange={e => setCapacityFloor(e.target.value)} placeholder="e.g. 70" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="installDate">Install Date</Label>
              <Input id="installDate" type="date" value={installDate} onChange={e => setInstallDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="warrantyStartDate">Start Date</Label>
              <Input id="warrantyStartDate" type="date" value={warrantyStartDate} onChange={e => setWarrantyStartDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="warrantyEndDate">End Date</Label>
              <Input id="warrantyEndDate" type="date" value={warrantyEndDate} onChange={e => setWarrantyEndDate(e.target.value)} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="registrationUrl">Registration URL</Label>
            <Input id="registrationUrl" value={registrationUrl} onChange={e => setRegistrationUrl(e.target.value)} placeholder="https://..." />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="coverageDetails">Coverage Details</Label>
            <Textarea id="coverageDetails" value={coverageDetails} onChange={e => setCoverageDetails(e.target.value)} rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={create.isPending || update.isPending}>
            {isEdit ? 'Save Changes' : 'Add Warranty'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
